import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ButtonDisabledState } from '@shared/types/button-state-tracking';
import {
  tap,
  take,
  map,
  catchError,
  withLatestFrom,
  filter,
} from 'rxjs/operators';
import { UserAction } from '@longnecktech/splash-commons-fe';
import { SessionService } from './session.service';
import { Game } from '@shared/types/game';
import { NextSpinInfo } from '@shared/types/next-spin-info';
import { environment } from '@environment';
import { Spin } from '@shared/types/spin';
import { CollectiblesService } from './collectibles.service';
import {
  Observable,
  BehaviorSubject,
  throwError,
  delay,
  timer,
  Subscription,
} from 'rxjs';
import { DELAY_ON_SPIN } from '@shared/constants/common.const';

@Injectable({
  providedIn: 'root',
})
export class SpinService {
  private http = inject(HttpClient);
  private session = inject(SessionService);
  private collectibleService = inject(CollectiblesService);

  private _spinInfo = new BehaviorSubject<NextSpinInfo | undefined>(undefined);
  private _showNextSpinInfo = new BehaviorSubject<boolean>(false);
  private _spin = new BehaviorSubject<Spin | undefined>(undefined);
  private _spinClicked = new BehaviorSubject<boolean>(false);
  private timerSubscription?: Subscription;
  private _fetchSpinInfoError = new BehaviorSubject<string | undefined>(
    undefined,
  );

  spinInfo$ = this._spinInfo.asObservable();
  allowedToSpin$ = this.spinInfo$.pipe(map((info) => !!info?.allowedToSpin));
  showNextSpinInfo$ = this._showNextSpinInfo.asObservable();
  spin$ = this._spin.asObservable();
  spinClicked$ = this._spinClicked.asObservable();
  isWon$ = this.spin$.pipe(
    filter((spin) => !!spin),
    map((spins) => !!spins!.winnings.length),
  );
  fetchSpinInfoError$ = this._fetchSpinInfoError.asObservable();

  fetchSpinInfo(game: Game): Observable<NextSpinInfo> {
    this._fetchSpinInfoError.next(undefined);
    return this.http
      .get<NextSpinInfo>(
        environment.backendUrl + '/api/spinmachine/spin/' + game.uuid,
      )
      .pipe(
        tap((data) => {
          if (data.nextSpinInSeconds > 0) {
            this.updateShowNextSpinInfo(true);
          }

          this.setSpinInfo({
            ...data,
            startTs: new Date().getTime(),
          });
          this.updateAllowedToSpinAfterDelay(data.nextSpinInSeconds);
        }),
        catchError((error) => {
          const errorMessage =
            error?.error?.message || error?.message || 'Unknown error occurred';
          this._fetchSpinInfoError.next(errorMessage);
          throw error;
        }),
      );
  }

  onSpin(game: Game): Observable<Spin> {
    this._spinClicked.next(true);
    return this.http
      .post<Spin>(
        environment.backendUrl + '/api/spinmachine/spin/' + game.uuid,
        {},
      )
      .pipe(
        withLatestFrom(this.session.user$.pipe(take(1))),
        map(([spin, user]) => {
          if (game!.spinPrize) {
            user!.balance -= game!.spinPrize;
          }
          this.setSpin(spin);
          this.setSpinInfo({
            ...spin.nextSpinInfo,
            startTs: new Date().getTime(),
          });
          this.updateAllowedToSpinAfterDelay(
            spin.nextSpinInfo.nextSpinInSeconds,
          );
          return spin;
        }),
        delay(DELAY_ON_SPIN),
        tap((spin) => {
          if (spin.nextSpinInfo.nextSpinInSeconds > 0) {
            this.updateShowNextSpinInfo(true);
          }

          this.collectibleService.updateCollectibles(
            spin.updatedCollectibles,
            true,
          );
        }),
        catchError((error: Error) => {
          this.session
            .sendAction(UserAction.TRANSACTION_ERROR, error)
            .pipe(take(1))
            .subscribe();
          return throwError(() => new Error(error.message));
        }),
      );
  }

  setSpinInfo(info: NextSpinInfo) {
    this._spinInfo.next(info);
  }

  updateShowNextSpinInfo(data: boolean) {
    this._showNextSpinInfo.next(data);
  }

  setSpin(data: Spin | undefined): void {
    this._spin.next(data);
  }

  sendUserMetrics(payload: ButtonDisabledState): Observable<void> {
    const url = 'https://user-metrics.splash.tech/api/v1/metric';
    return this.http.post<void>(url, payload);
  }

  private updateAllowedToSpinAfterDelay(seconds: number): void {
    this.timerSubscription?.unsubscribe();

    if (seconds <= 0) {
      this.setSpinInfo({
        ...this._spinInfo.getValue()!,
        allowedToSpin: true,
      });
      this.updateShowNextSpinInfo(false);
    } else {
      this.timerSubscription = timer(seconds * 1000).subscribe(() => {
        const currentInfo = this._spinInfo.getValue();
        if (currentInfo) {
          this.setSpinInfo({
            ...currentInfo,
            allowedToSpin: true,
          });
          this.updateShowNextSpinInfo(false);
        }
      });
    }
  }
}
